import React, { useState, useEffect, useRef } from "react";
import {
  Popover,
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  ToggleButton,
  Paper,
  Grid,
  IconButton,
  Divider,
  ClickAwayListener,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import moment from "moment";
import ArrowBackIosOutlinedIcon from "@mui/icons-material/ArrowBackIosOutlined";
import ArrowForwardIosOutlinedIcon from "@mui/icons-material/ArrowForwardIosOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

interface RecurringTaskPopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onSave: (recurringConfig: RecurringConfig) => void;
  initialConfig?: RecurringConfig;
  availableStatus: Array<{ _id: string; status: string; color?: string }>;
}

export interface RecurringConfig {
  isRecurring: boolean;
  frequency: "Daily" | "Weekly" | "Monthly";
  trigger: "When done" | "On schedule";
  skipWeekends?: boolean;
  createNewTask?: boolean;
  updateStatusTo?: string;
  repeatTimes?: number;
  customInterval?: number;
  customUnit?: "day" | "week" | "month";
  selectedDays?: string[];
  selectedMonthlyDays?: number[];
  monthlyOption?: "same_day" | "third_tuesday" | "first_day" | "last_day";
  startDate?: Date;
  endOnDate?: Date;
  isEndOn?: boolean;
  selectedTime?: string; // This will be in UTC format
}

const RecurringTaskPopover: React.FC<RecurringTaskPopoverProps> = ({
  open,
  anchorEl,
  onClose,
  onSave,
  initialConfig,
  availableStatus,
}) => {
  const { t } = useTranslation(["bugherd"]);

  // Utility functions for timezone conversion
  const convertUTCToLocal = (utcTime: string): string => {
    if (!utcTime) return "09:00";
    const [hours, minutes] = utcTime.split(":");
    const utcMoment = moment
      .utc()
      .hours(parseInt(hours))
      .minutes(parseInt(minutes));
    return utcMoment.local().format("HH:mm");
  };

  const convertLocalToUTC = (localTime: string): string => {
    if (!localTime) return "09:00";
    const [hours, minutes] = localTime.split(":");
    const localMoment = moment()
      .hours(parseInt(hours))
      .minutes(parseInt(minutes));
    return localMoment.utc().format("HH:mm");
  };

  const convertUTCDateToLocal = (utcDate: Date): Date => {
    return moment.utc(utcDate).local().toDate();
  };

  const convertLocalDateToUTC = (localDate: Date): Date => {
    return moment(localDate).utc().toDate();
  };

  // Function to calculate default repeat times based on frequency and options
  const calculateDefaultRepeatTimes = (
    frequency: string,
    skipWeekends: boolean = false,
    selectedDays: string[] = []
  ) => {
    switch (frequency) {
      case "Daily":
        if (skipWeekends) {
          return 260;
        }
        return 365;

      case "Weekly":
        if (selectedDays.length === 0) {
          return 52;
        }
        return 52 * selectedDays.length;

      case "Monthly":
        return 12;

      default:
        return 365;
    }
  };

  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [currentMonth, setCurrentMonth] = useState(moment());
  const [repeatOption, setRepeatOption] = useState<"times" | "endOn">("times");
  const [endOnCalendarOpen, setEndOnCalendarOpen] = useState(false);
  const [endOnCurrentMonth, setEndOnCurrentMonth] = useState(moment());
  const [timePickerOpen, setTimePickerOpen] = useState(false);
  const [showAddTime, setShowAddTime] = useState(false);
  const [endOnButtonRef, setEndOnButtonRef] = useState<HTMLElement | null>(
    null
  );
  const timePickerRef = useRef<HTMLDivElement>(null);

  // Local time state for UI display
  const [localSelectedTime, setLocalSelectedTime] = useState<string>("09:00");

  const [config, setConfig] = useState<RecurringConfig>({
    isRecurring: true, // Set default to true
    frequency: "Daily",
    trigger: "When done",
    skipWeekends: false,
    createNewTask: true,
    updateStatusTo: availableStatus[0]?._id || "",
    repeatTimes: calculateDefaultRepeatTimes("Daily", false, []), // Use the function for initial value
    customInterval: 1,
    customUnit: "week",
    selectedDays: [],
    selectedMonthlyDays: [],
    monthlyOption: "same_day",
    startDate: new Date(),
    endOnDate: new Date(),
    isEndOn: false,
    selectedTime: convertLocalToUTC("09:00"), // Store in UTC
  });

  useEffect(() => {
    if (initialConfig) {
      // Convert UTC times to local for display
      const localTime = initialConfig.selectedTime
        ? convertUTCToLocal(initialConfig.selectedTime)
        : "09:00";

      setLocalSelectedTime(localTime);

      const configToSet = {
        ...initialConfig,
        isRecurring: true, // Always ensure isRecurring is true when modal opens
        startDate: initialConfig.startDate
          ? convertUTCDateToLocal(initialConfig.startDate)
          : new Date(),
        endOnDate: initialConfig.endOnDate
          ? convertUTCDateToLocal(initialConfig.endOnDate)
          : new Date(),
      };

      setConfig(configToSet);

      if (configToSet.startDate) {
        setSelectedDate(configToSet.startDate);
      }
      if (initialConfig.isEndOn) {
        setRepeatOption("endOn");
      }
    } else {
      // Set defaults for new recurring config
      const defaultLocalTime = "09:00";
      setLocalSelectedTime(defaultLocalTime);
      setConfig((prev) => ({
        ...prev,
        isRecurring: true,
        updateStatusTo: availableStatus[0]?._id || "",
        repeatTimes: calculateDefaultRepeatTimes("Daily", false, []),
        selectedTime: convertLocalToUTC(defaultLocalTime),
      }));
    }
  }, [initialConfig, availableStatus]);

  const handleFrequencyChange = (value: string) => {
    const newRepeatTimes = calculateDefaultRepeatTimes(
      value,
      config.skipWeekends,
      config.selectedDays
    );

    setConfig((prev) => ({
      ...prev,
      frequency: value as "Daily" | "Weekly" | "Monthly",
      selectedDays: value === "Weekly" ? [] : prev.selectedDays,
      selectedMonthlyDays: value === "Monthly" ? [] : prev.selectedMonthlyDays,
      repeatTimes: newRepeatTimes,
    }));
  };

  const handleTriggerChange = (value: string) => {
    setConfig((prev) => ({
      ...prev,
      trigger: value as "When done" | "On schedule",
    }));
  };

  const handleDayToggle = (day: string) => {
    const newSelectedDays = config.selectedDays?.includes(day)
      ? config.selectedDays.filter((d) => d !== day)
      : [...(config.selectedDays || []), day];

    // Recalculate repeat times based on new selected days
    const newRepeatTimes = calculateDefaultRepeatTimes(
      config.frequency,
      config.skipWeekends,
      newSelectedDays
    );

    setConfig((prev) => ({
      ...prev,
      selectedDays: newSelectedDays,
      repeatTimes: newRepeatTimes,
    }));
  };

  const handleSkipWeekendsChange = (checked: boolean) => {
    const newRepeatTimes = calculateDefaultRepeatTimes(
      config.frequency,
      checked,
      config.selectedDays
    );

    setConfig((prev) => ({
      ...prev,
      skipWeekends: checked,
      repeatTimes: newRepeatTimes,
    }));
  };

  const handleMonthlyOptionChange = (value: string) => {
    setConfig((prev) => ({
      ...prev,
      monthlyOption: value as
        | "same_day"
        | "third_tuesday"
        | "first_day"
        | "last_day",
    }));
  };

  const handleRepeatOptionChange = (value: string) => {
    setRepeatOption(value as "times" | "endOn");
    setConfig((prev) => ({
      ...prev,
      isEndOn: value === "endOn",
      // Clear the opposite value when switching
      ...(value === "endOn" ? { repeatTimes: null } : { endOnDate: null }),
    }));
  };

  const handleRepeatTimesChange = (value: number) => {
    setConfig((prev) => ({
      ...prev,
      repeatTimes: value,
      // Ensure endOnDate is null when using repeat times
      endOnDate: null,
      isEndOn: false,
    }));
  };

  const handleEndOnDateChange = (date: Date) => {
    setConfig((prev) => ({
      ...prev,
      endOnDate: date, // Keep as local date in state for UI
      // Ensure repeatTimes is null when using end date
      repeatTimes: null,
      isEndOn: true,
    }));
  };

  const handleTimeChange = (localTime: string) => {
    setLocalSelectedTime(localTime);

    // Convert local time to UTC for storage
    const utcTime = convertLocalToUTC(localTime);

    setConfig((prev) => ({
      ...prev,
      selectedTime: utcTime,
    }));

    // Update the selected date with the new local time
    const [hours, minutes] = localTime.split(":");
    const newDate = moment(selectedDate)
      .hours(parseInt(hours))
      .minutes(parseInt(minutes))
      .toDate();
    setSelectedDate(newDate);
  };

  const handleSave = () => {
    const finalConfig = {
      ...config,
      // Convert local dates to UTC before saving
      startDate: convertLocalDateToUTC(selectedDate),
      endOnDate: config.endOnDate
        ? convertLocalDateToUTC(config.endOnDate)
        : null,
      isRecurring: true, // Ensure isRecurring is always true when saving
      // Ensure only one option is set based on current selection
      repeatTimes: repeatOption === "times" ? config.repeatTimes : null,
      isEndOn: repeatOption === "endOn",
      // selectedTime is already in UTC from handleTimeChange
    };

    onSave(finalConfig);
  };

  const handleCancel = () => {
    onClose();
  };

  const handleTimePickerClickAway = () => {
    setTimePickerOpen(false);
  };

  const handleEndOnCalendarClickAway = () => {
    setEndOnCalendarOpen(false);
  };

  const weekDays = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

  const getCalendarHighlightedDates = () => {
    const today = moment();
    const highlightedDates = [];

    if (config.frequency === "Daily") {
      for (let i = 0; i < 30; i++) {
        const date = moment(selectedDate).add(i, "days");
        if (config.skipWeekends && (date.day() === 0 || date.day() === 6)) {
          continue;
        }
        highlightedDates.push(date.format("YYYY-MM-DD"));
      }
    } else if (config.frequency === "Weekly") {
      for (let i = 0; i < 12; i++) {
        config.selectedDays?.forEach((dayIndex) => {
          const dayNum = weekDays.indexOf(dayIndex);
          const date = moment(selectedDate).add(i, "weeks").day(dayNum);
          if (date.isAfter(today)) {
            highlightedDates.push(date.format("YYYY-MM-DD"));
          }
        });
      }
    } else if (config.frequency === "Monthly") {
      for (let i = 0; i < 12; i++) {
        if (config.monthlyOption === "same_day") {
          const date = moment(selectedDate).add(i, "months");
          if (date.isAfter(today)) {
            highlightedDates.push(date.format("YYYY-MM-DD"));
          }
        }
        // Add logic for other monthly options as needed
      }
    }

    return highlightedDates;
  };

  const shouldHighlightDate = (date: moment.Moment) => {
    const highlightedDates = getCalendarHighlightedDates();
    return highlightedDates.includes(date.format("YYYY-MM-DD"));
  };

  // Generate time slots in local timezone
  const generateTimeSlots = () => {
    const timeSlots = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const time = moment().hour(hour).minute(minute);
        timeSlots.push({
          value: time.format("HH:mm"), // Local time format
          label: time.format("h:mm a"), // Display format
        });
      }
    }
    return timeSlots;
  };

  // Time Picker Component
  const TimePicker = () => {
    const timeSlots = generateTimeSlots();

    return (
      <ClickAwayListener onClickAway={handleTimePickerClickAway}>
        <Box
          ref={timePickerRef}
          sx={{
            position: "absolute",
            top: "100%",
            left: 0,
            zIndex: 1200,
            backgroundColor: "white",
            border: "1px solid #e0e0e0",
            borderRadius: 1,
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
            maxHeight: 300,
            width: 180,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 1,
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Typography variant="body2" sx={{ color: "#666" }}>
              {t("Select time")}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setTimePickerOpen(false)}
              sx={{ color: "#666" }}
            >
              <KeyboardReturnIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box
            sx={{
              maxHeight: 250,
              overflowY: "auto",
            }}
          >
            {timeSlots.map((slot) => (
              <Box
                key={slot.value}
                onClick={() => {
                  handleTimeChange(slot.value); // Pass local time
                  setTimePickerOpen(false);
                }}
                sx={{
                  p: 1.5,
                  cursor: "pointer",
                  backgroundColor:
                    localSelectedTime === slot.value
                      ? "#f0f0f0"
                      : "transparent",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                  fontSize: "0.875rem",
                }}
              >
                {slot.label}
              </Box>
            ))}
          </Box>
        </Box>
      </ClickAwayListener>
    );
  };

  // End On Date Calendar Component
  const EndOnCalendar = () => {
    const startOfMonth = endOnCurrentMonth.clone().startOf("month");
    const endOfMonth = endOnCurrentMonth.clone().endOf("month");
    const startOfCalendar = startOfMonth.clone().startOf("week");
    const endOfCalendar = endOfMonth.clone().endOf("week");

    const calendarDays = [];
    let day = startOfCalendar.clone();

    while (day.isSameOrBefore(endOfCalendar, "day")) {
      calendarDays.push(day.clone());
      day.add(1, "day");
    }

    const weeks = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
      weeks.push(calendarDays.slice(i, i + 7));
    }

    const handlePrevMonth = () => {
      setEndOnCurrentMonth((prev) => prev.clone().subtract(1, "month"));
    };

    const handleNextMonth = () => {
      setEndOnCurrentMonth((prev) => prev.clone().add(1, "month"));
    };

    const handleDateClick = (date: moment.Moment) => {
      handleEndOnDateChange(date.toDate()); // Pass as local date
    };

    const handleDone = () => {
      setEndOnCalendarOpen(false);
    };

    return (
      <ClickAwayListener onClickAway={handleEndOnCalendarClickAway}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1300,
            backgroundColor: "white",
            border: "1px solid #e0e0e0",
            borderRadius: 1,
            boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.2)",
            width: 400,
          }}
        >
          {/* Calendar Header */}
          <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <CalendarTodayOutlinedIcon
                sx={{ color: "#666", fontSize: "1.2rem" }}
              />
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {config.endOnDate
                  ? moment(config.endOnDate).format("M/D/YY") // Display in local timezone
                  : moment().format("M/D/YY")}
              </Typography>
              <CloseIcon sx={{ color: "#666", fontSize: "1rem" }} />
              <Button
                variant="text"
                sx={{
                  color: "#666",
                  textTransform: "none",
                  fontSize: "0.875rem",
                  minWidth: "auto",
                  p: 0.5,
                }}
              >
                Add time
              </Button>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <IconButton size="small" onClick={handlePrevMonth}>
                  <ArrowBackIosOutlinedIcon fontSize="small" />
                </IconButton>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, minWidth: 100 }}
                >
                  {endOnCurrentMonth.format("MMMM")}
                </Typography>
                <IconButton size="small" onClick={handleNextMonth}>
                  <ArrowForwardIosOutlinedIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {t("Today")}
                </Typography>
                <IconButton size="small">
                  <KeyboardArrowUpIcon fontSize="small" />
                </IconButton>
                <IconButton size="small">
                  <KeyboardArrowDownIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </Box>

          {/* Calendar Content */}
          <Box sx={{ p: 2 }}>
            {/* Day Headers */}
            <Grid container sx={{ mb: 1 }}>
              {weekDays.map((day) => (
                <Grid key={day} item xs={12 / 7} sx={{ textAlign: "center" }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: "#666",
                      fontSize: "0.75rem",
                    }}
                  >
                    {day}
                  </Typography>
                </Grid>
              ))}
            </Grid>

            {/* Calendar Grid */}
            <Box sx={{ mb: 3 }}>
              {weeks.map((week, weekIndex) => (
                <Grid container key={weekIndex} sx={{ mb: 0.5 }}>
                  {week.map((day, dayIndex) => {
                    const isToday = day.isSame(moment(), "day");
                    const isSelected = day.isSame(
                      moment(config.endOnDate),
                      "day"
                    );
                    const isCurrentMonth = day.isSame(
                      endOnCurrentMonth,
                      "month"
                    );

                    return (
                      <Grid
                        key={dayIndex}
                        item
                        xs={12 / 7}
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: 40,
                        }}
                      >
                        <Box
                          onClick={() => handleDateClick(day)}
                          sx={{
                            width: 36,
                            height: 36,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: "50%",
                            cursor: "pointer",
                            fontSize: "0.875rem",
                            fontWeight: isSelected ? 600 : 400,
                            backgroundColor: isToday
                              ? "#d32f2f"
                              : isSelected
                              ? "#9c27b0"
                              : "transparent",
                            color:
                              isToday || isSelected
                                ? "white"
                                : isCurrentMonth
                                ? "#333"
                                : "#ccc",
                            "&:hover": {
                              backgroundColor:
                                isToday || isSelected ? undefined : "#f5f5f5",
                            },
                            transition: "all 0.2s ease",
                          }}
                        >
                          {day.date()}
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              ))}
            </Box>

            {/* Done Button */}
            <Button
              variant="contained"
              fullWidth
              onClick={handleDone}
              sx={{
                backgroundColor: "#9c27b0",
                "&:hover": {
                  backgroundColor: "#7b1fa2",
                },
                py: 1.5,
                fontSize: "1rem",
                fontWeight: 500,
              }}
            >
              Done
            </Button>
          </Box>
        </Box>
      </ClickAwayListener>
    );
  };

  // Custom Calendar Component
  const CustomCalendar = () => {
    const startOfMonth = currentMonth.clone().startOf("month");
    const endOfMonth = currentMonth.clone().endOf("month");
    const startOfCalendar = startOfMonth.clone().startOf("week");
    const endOfCalendar = endOfMonth.clone().endOf("week");

    const calendarDays = [];
    let day = startOfCalendar.clone();

    while (day.isSameOrBefore(endOfCalendar, "day")) {
      calendarDays.push(day.clone());
      day.add(1, "day");
    }

    const weeks = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
      weeks.push(calendarDays.slice(i, i + 7));
    }

    const handlePrevMonth = () => {
      setCurrentMonth((prev) => prev.clone().subtract(1, "month"));
    };

    const handleNextMonth = () => {
      setCurrentMonth((prev) => prev.clone().add(1, "month"));
    };

    const handleDateClick = (date: moment.Moment) => {
      const [hours, minutes] = localSelectedTime.split(":"); // Use local time
      const newDate = date
        .clone()
        .hours(parseInt(hours))
        .minutes(parseInt(minutes))
        .toDate();
      setSelectedDate(newDate);
    };

    return (
      <Box>
        {/* Date and Time Display */}
        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <CalendarTodayOutlinedIcon
            sx={{ color: "#666", fontSize: "1.2rem" }}
          />
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {moment(selectedDate).format("M/D/YY")}{" "}
            {/* Display in local timezone */}
          </Typography>
          <CloseIcon sx={{ color: "#666", fontSize: "1rem" }} />
          {showAddTime ? (
            <Box sx={{ position: "relative" }}>
              <Button
                variant="text"
                onClick={() => setTimePickerOpen(!timePickerOpen)}
                sx={{
                  color: "#666",
                  textTransform: "none",
                  fontSize: "0.875rem",
                  minWidth: "auto",
                  p: 0.5,
                }}
              >
                {localSelectedTime
                  ? moment(localSelectedTime, "HH:mm").format("h:mm a") // Display local time
                  : "Add time"}
              </Button>
              {timePickerOpen && <TimePicker />}
            </Box>
          ) : (
            <Button
              variant="text"
              onClick={() => setShowAddTime(true)}
              sx={{
                color: "#666",
                textTransform: "none",
                fontSize: "0.875rem",
                minWidth: "auto",
                p: 0.5,
              }}
            >
              Add time
            </Button>
          )}
        </Box>

        {/* Calendar Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            px: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton size="small" onClick={handlePrevMonth}>
              <ArrowBackIosOutlinedIcon fontSize="small" />
            </IconButton>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, minWidth: 120, textAlign: "center" }}
            >
              {currentMonth.format("MMMM YYYY")}
            </Typography>
            <IconButton size="small" onClick={handleNextMonth}>
              <ArrowForwardIosOutlinedIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {t("Today")}
          </Typography>
        </Box>

        {/* Day Headers */}
        <Grid container sx={{ mb: 1 }}>
          {weekDays.map((day) => (
            <Grid key={day} item xs={12 / 7} sx={{ textAlign: "center" }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: "#666",
                  fontSize: "0.75rem",
                }}
              >
                {day}
              </Typography>
            </Grid>
          ))}
        </Grid>

        {/* Calendar Grid */}
        <Box>
          {weeks.map((week, weekIndex) => (
            <Grid container key={weekIndex} sx={{ mb: 0.5 }}>
              {week.map((day, dayIndex) => {
                const isToday = day.isSame(moment(), "day");
                const isSelected = day.isSame(moment(selectedDate), "day");
                const isHighlighted = shouldHighlightDate(day);
                const isCurrentMonth = day.isSame(currentMonth, "month");

                return (
                  <Grid
                    key={dayIndex}
                    item
                    xs={12 / 7}
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 36,
                    }}
                  >
                    <Box
                      onClick={() => handleDateClick(day)}
                      sx={{
                        width: 32,
                        height: 32,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "50%",
                        cursor: "pointer",
                        fontSize: "0.8rem",
                        fontWeight: isSelected ? 600 : 400,
                        backgroundColor: isToday
                          ? "#d32f2f"
                          : isHighlighted
                          ? "#e3f2fd"
                          : isSelected
                          ? "#1976d2"
                          : "transparent",
                        color:
                          isToday || isSelected
                            ? "white"
                            : isHighlighted
                            ? "#1976d2"
                            : isCurrentMonth
                            ? "#333"
                            : "#ccc",
                        "&:hover": {
                          backgroundColor:
                            isToday || isSelected
                              ? undefined
                              : isHighlighted
                              ? "#bbdefb"
                              : "#f5f5f5",
                        },
                        transition: "all 0.2s ease",
                      }}
                    >
                      {day.date()}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      PaperProps={{
        sx: {
          width: "560px",
          maxWidth: "90vw",
          maxHeight: "80vh",
          overflow: "hidden",
          border: "1px solid #e0e0e0",
          boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.12)",
        },
      }}
    >
      <Paper sx={{ p: 0, position: "relative" }}>
        {/* Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: "1px solid #e0e0e0",
            backgroundColor: "#f5f5f5",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {t("Task Occur automatically without manual recreation")}
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button onClick={handleCancel} size="small" variant="outlined">
              {t("Cancel")}
            </Button>
            <Button onClick={handleSave} size="small" variant="contained">
              {t("Save")}
            </Button>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ display: "flex", maxHeight: "500px" }}>
          {/* Left Panel - Configuration */}
          <Box
            sx={{
              width: "300px",
              p: 2,
              borderRight: "1px solid #e0e0e0",
              overflowY: "auto",
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              {t("Recurring")}
            </Typography>

            {/* Frequency Selection */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Select
                value={config.frequency}
                onChange={(e) => handleFrequencyChange(e.target.value)}
                size="small"
                displayEmpty
              >
                <MenuItem value="Daily">{t("Daily")}</MenuItem>
                <MenuItem value="Weekly">{t("Weekly")}</MenuItem>
                <MenuItem value="Monthly">{t("Monthly")}</MenuItem>
              </Select>
            </FormControl>

            {/* Monthly Options */}
            {config.frequency === "Monthly" && (
              <Box sx={{ mb: 2 }}>
                <FormControl fullWidth>
                  <Select
                    value={config.monthlyOption}
                    onChange={(e) => handleMonthlyOptionChange(e.target.value)}
                    size="small"
                    displayEmpty
                  >
                    <MenuItem value="same_day">
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <span>{t("Same day each month")}</span>
                        <span style={{ color: "#9c27b0" }}>✓</span>
                      </Box>
                    </MenuItem>
                    <MenuItem value="third_tuesday">
                      {t("Third Tuesday")}
                    </MenuItem>
                    <MenuItem value="first_day">
                      {t("First day of the month")}
                    </MenuItem>
                    <MenuItem value="last_day">
                      {t("Last day of the month")}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}

            {/* Weekly Day Selection */}
            {config.frequency === "Weekly" && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {t("Select Days")}
                </Typography>
                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                  {weekDays.map((day) => (
                    <ToggleButton
                      key={day}
                      value={day}
                      selected={config.selectedDays?.includes(day)}
                      onChange={() => handleDayToggle(day)}
                      size="small"
                      sx={{
                        width: 35,
                        height: 35,
                        fontSize: "0.7rem",
                        border: "1px solid #d6d6d6",
                        "&.Mui-selected": {
                          backgroundColor: "#e3f2fd",
                          color: "#1976d2",
                        },
                      }}
                    >
                      {day}
                    </ToggleButton>
                  ))}
                </Box>
              </Box>
            )}

            {/* Trigger Selection */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Select
                value={config.trigger}
                onChange={(e) => handleTriggerChange(e.target.value)}
                size="small"
                displayEmpty
              >
                <MenuItem value="When done">{t("When done")}</MenuItem>
                <MenuItem value="On schedule">{t("On schedule")}</MenuItem>
              </Select>
            </FormControl>

            {/* Skip Weekends Option for Daily */}
            {config.frequency === "Daily" && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={config.skipWeekends}
                    onChange={(e) => handleSkipWeekendsChange(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2">{t("Skip weekends")}</Typography>
                }
                sx={{ mb: 2 }}
              />
            )}

            {/* Create New Task Option */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={config.createNewTask}
                  onChange={(e) =>
                    setConfig((prev) => ({
                      ...prev,
                      createNewTask: e.target.checked,
                    }))
                  }
                  size="small"
                />
              }
              label={
                <Typography variant="body2">{t("Create new task")}</Typography>
              }
              sx={{ mb: 2 }}
            />

            {/* Update Status Option */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!config.updateStatusTo}
                  onChange={(e) =>
                    setConfig((prev) => ({
                      ...prev,
                      updateStatusTo: e.target.checked
                        ? availableStatus[0]?._id || ""
                        : "",
                    }))
                  }
                  size="small"
                />
              }
              label={
                <Typography variant="body2">
                  {t("Update status to:")}
                </Typography>
              }
              sx={{ mb: 1 }}
            />

            {config.updateStatusTo && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <Select
                  value={config.updateStatusTo}
                  onChange={(e) =>
                    setConfig((prev) => ({
                      ...prev,
                      updateStatusTo: e.target.value,
                    }))
                  }
                  size="small"
                >
                  {availableStatus.map((status) => (
                    <MenuItem key={status._id} value={status._id}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            backgroundColor: status.color || "#f44336",
                            borderRadius: "2px",
                          }}
                        />
                        {status.status}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Repeat Options */}
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 2 }}>
              <FormControl sx={{ minWidth: 80 }}>
                <Select
                  value={repeatOption}
                  onChange={(e) => handleRepeatOptionChange(e.target.value)}
                  size="small"
                >
                  <MenuItem value="times">{t("Repeat")}</MenuItem>
                  <MenuItem value="endOn">{t("End On")}</MenuItem>
                </Select>
              </FormControl>

              {repeatOption === "times" ? (
                <>
                  <TextField
                    type="number"
                    value={config.repeatTimes || ""}
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      if (!isNaN(value) && value > 0) {
                        handleRepeatTimesChange(value);
                      }
                    }}
                    size="small"
                    sx={{ width: 80 }}
                    inputProps={{ min: 1 }}
                  />
                  <Typography variant="body2">{t("times")}</Typography>
                </>
              ) : (
                <Button
                  ref={setEndOnButtonRef}
                  variant="outlined"
                  size="small"
                  onClick={() => setEndOnCalendarOpen(!endOnCalendarOpen)}
                  startIcon={<CalendarTodayOutlinedIcon />}
                  sx={{ minWidth: 140 }}
                >
                  {config.endOnDate
                    ? moment(config.endOnDate).format("MMM DD, YYYY") // Display in local timezone
                    : t("Select Date")}
                </Button>
              )}
            </Box>
          </Box>

          {/* Right Panel - Calendar */}
          <Box
            sx={{
              flex: 1,
              p: 2,
              display: "flex",
              flexDirection: "column",
              maxHeight: "500px",
              overflowY: "auto",
            }}
          >
            <CustomCalendar />
          </Box>
        </Box>

        {/* End On Calendar Overlay */}
        {endOnCalendarOpen && <EndOnCalendar />}
      </Paper>
    </Popover>
  );
};

export default RecurringTaskPopover;
